import React, { useEffect, useState } from 'react'
import { useAuth } from '../../contexts/AuthContextLazy'
import { supabase } from '../../lib/supabase-auth'
import { PlusIcon, FolderIcon, ChevronDownIcon, UserIcon, CogIcon, CalendarDaysIcon, ClockIcon, EyeIcon } from '@heroicons/react/24/outline'
import { ProjectTemplate } from '../../utils/project-templates'
import { ProjectSetupWizard } from '../../components/portal/ProjectSetupWizard'
import { TemplatePreview } from '../../components/portal/TemplatePreview'
import { colors } from '../../lib/colors'

interface Project {
  id: string
  name: string
  description: string | null
  status: 'active' | 'completed' | 'on_hold' | 'cancelled'
  start_date: string | null
  end_date: string | null
  created_at: string
  client_id: string | null
  party?: {
    id: string
    name: string
  }
}

interface Party {
  id: string
  name: string
  type: string
  status: string
}

export const Projects: React.FC = () => {
  const { organization, canAccess, userProfile } = useAuth()
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showSetupWizard, setShowSetupWizard] = useState(false)
  const [clients, setClients] = useState<Party[]>([])
  const [templates, setTemplates] = useState<ProjectTemplate[]>([])
  const [loadingClients, setLoadingClients] = useState(false)
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [useTemplate, setUseTemplate] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<string>('')
  const [modalAutoOpened, setModalAutoOpened] = useState(false)
  const [showTemplatePreview, setShowTemplatePreview] = useState(false)
  const [previewTemplate, setPreviewTemplate] = useState<any>(null)

  // Get clientId from URL query parameters
  const searchParams = new URLSearchParams(window.location.search)
  const preSelectedClientId = searchParams.get('clientId')

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active' as const,
    start_date: '',
    end_date: '',
    client_id: ''
  })

  useEffect(() => {
    if (organization) {
      fetchProjects()
    }
  }, [organization])

  // Set pre-selected client when modal opens
  useEffect(() => {
    if (showCreateModal && preSelectedClientId) {
      setFormData(prev => ({ ...prev, client_id: preSelectedClientId }))
    }
  }, [showCreateModal, preSelectedClientId])

  // Auto-open wizard if clientId is present in URL on initial load
  useEffect(() => {
    if (preSelectedClientId && !loading && !modalAutoOpened) {
      setModalAutoOpened(true)
      setShowSetupWizard(true)
    }
  }, [preSelectedClientId, loading, modalAutoOpened])

  const fetchProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          party:parties!client_id (
            id,
            name
          )
        `)
        .eq('organization_id', organization!.id)
        .order('created_at', { ascending: false })

      if (error) throw error
      setProjects(data || [])
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error fetching projects:', error);
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchClients = async () => {
    setLoadingClients(true)
    try {
      // Check if user is authenticated
      const { data: { user }, error: authError } = await supabase.auth.getUser()
      if (authError || !user) {
        console.error('Authentication error:', authError);
        throw new Error('User not authenticated')
      }

      const { data, error } = await supabase
        .from('parties')
        .select('id, name, type, status')
        .eq('status', 'active')
        .order('name')

      if (error) {
        console.error('Error fetching parties:', error);
        throw error
      }

      if (import.meta.env.DEV) {
        console.log('Fetched clients:', data);
      }
      setClients(data || [])
      if (import.meta.env.DEV) {
        console.log(`Successfully loaded ${data?.length || 0} clients`);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error fetching clients:', error);
      }
      // Don't show alert for every error, just log it
      setClients([])
    } finally {
      setLoadingClients(false)
    }
  }

  const fetchTemplates = async () => {
    setLoadingTemplates(true)
    try {
      // First try to fetch from project_templates table
      const { data: tableTemplates, error: tableError } = await supabase
        .from('project_templates')
        .select('*')
        .eq('is_active', true)
        .order('name')

      if (!tableError && tableTemplates && tableTemplates.length > 0) {
        setTemplates(tableTemplates)
      } else {
        // Fallback to RPC function for templates stored in platform_accounts
        const { data: rpcTemplates, error: rpcError } = await supabase
          .rpc('get_project_templates')

        if (rpcError) throw rpcError

        // Transform RPC templates to match expected format
        const transformedTemplates = (rpcTemplates || []).map((t: any) => ({
          id: t.id,
          name: t.name,
          description: t.description,
          template_type: t.project_type,
          icon: t.icon,
          milestones: t.milestones,
          is_active: true
        }))

        setTemplates(transformedTemplates)
        if (import.meta.env.DEV) {
          console.log(`Loaded ${transformedTemplates.length} templates from RPC`);
        }
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error fetching templates:', error);
      }
      setTemplates([])
    } finally {
      setLoadingTemplates(false)
    }
  }

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.client_id) {
      alert('Please select a client')
      return
    }

    try {
      if (useTemplate && selectedTemplate) {
        // Get the selected template to use its data
        const template = templates.find(t => t.id === selectedTemplate)

        // Use the RPC function to create project from template
        const { data: projectId, error: rpcError } = await supabase
          .rpc('create_project_from_template', {
            p_template_id: selectedTemplate,
            p_client_id: formData.client_id,
            p_advisor_id: userProfile?.id || null,
            p_project_name: formData.name,
            p_project_type: (() => {
              // Map template types to allowed project types
              const typeMap: Record<string, string> = {
                'insurance_application': 'insurance_policy',
                'estate_planning': 'estate_planning',
                'retirement_planning': 'retirement_planning',
                'investment_review': 'investment_transfer',
                'tax_planning': 'tax_planning',
                'business_succession': 'other',
                'risk_assessment': 'other',
                'financial_review': 'other',
                'custom': 'other'
              }
              return typeMap[template?.project_type || ''] || 'other'
            })(),
            p_priority: 'normal',
            p_intention: template?.description || '',
            p_expected_outcome: `Complete ${template?.name || 'project'}`,
            p_estimated_completion_date: formData.end_date || null,
            p_is_visible_to_client: true,
            p_template_milestones: template?.milestones ? JSON.stringify(template.milestones) : null
          })

        if (rpcError) throw rpcError

        // Update the project with organization_id if it was created successfully
        if (projectId) {
          const { error: updateError } = await supabase
            .from('projects')
            .update({ organization_id: organization!.id })
            .eq('id', projectId)

          if (updateError) throw updateError
        }
      } else {
        // Regular project creation
        const projectData: any = {
          organization_id: organization!.id,
          name: formData.name,
          description: formData.description || null,
          status: formData.status,
          client_id: formData.client_id,
          assigned_to: userProfile?.id || null,
          is_visible_to_client: true,
          project_type: 'other',
          priority: 'normal'
        }

        // Only add dates if they have values
        if (formData.start_date) {
          projectData.start_date = formData.start_date
        } else {
          projectData.start_date = new Date().toISOString().split('T')[0]
        }

        if (formData.end_date) {
          projectData.end_date = formData.end_date
        }

        if (import.meta.env.DEV) {
          console.log('Creating project with data:', projectData);
        }

        const { error } = await supabase
          .from('projects')
          .insert(projectData)

        if (error) throw error
      }

      setShowCreateModal(false)
      setFormData({
        name: '',
        description: '',
        status: 'active',
        start_date: '',
        end_date: '',
        client_id: ''
      })
      setUseTemplate(false)
      setSelectedTemplate('')
      fetchProjects()
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error creating project:', error);
      }
      alert('Failed to create project. Please try again.')
    }
  }

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplate(templateId)
    const template = templates.find(t => t.id === templateId)
    if (template) {
      setFormData(prev => ({
        ...prev,
        name: template.name,
        description: template.description
      }))
    }
  }

  const openCreateModal = () => {
    if (import.meta.env.DEV) {
      console.log('Opening create modal, fetching clients...');
    }
    setShowCreateModal(true)
    fetchClients()
    // Always fetch templates when opening modal
    fetchTemplates()
    // Auto-open modal if clientId is present in URL
    if (preSelectedClientId) {
      setFormData(prev => ({ ...prev, client_id: preSelectedClientId }))
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-400 border-green-500/30'
      case 'completed':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30'
      case 'on_hold':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
      case 'cancelled':
        return 'bg-red-500/20 text-red-400 border-red-500/30'
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="py-6" style={{ backgroundColor: colors.premium.darker }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="mb-6 flex justify-end gap-3">
          {canAccess('projects', 'create') && (
            <>
              <button
                type="button"
                onClick={openCreateModal}
                className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium text-white shadow-lg transition-all duration-200 hover:scale-105"
                style={{
                  background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)`,
                  boxShadow: `0 4px 12px -2px ${colors.brand.primary}40`
                }}
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                New Project
              </button>
              <button
                type="button"
                onClick={() => setShowSetupWizard(true)}
                className="inline-flex items-center justify-center rounded-lg border border-white/10 bg-white/5 backdrop-blur-sm px-4 py-2 text-sm font-medium text-gray-300 hover:bg-white/10 hover:text-white transition-all duration-200"
              >
                <CogIcon className="-ml-1 mr-2 h-5 w-5" />
                Advanced Setup
              </button>
            </>
          )}
        </div>

        <div className="mt-8">
          <div className="grid gap-4">
            {projects.map((project) => (
              <a
                key={project.id}
                href={`/portal/projects/${project.id}`}
                className="group block bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6 hover:bg-white/10 hover:border-white/20 transition-all duration-200 hover:scale-[1.02] hover:shadow-xl"
                style={{
                  boxShadow: '0 4px 24px -8px rgba(0, 0, 0, 0.3)'
                }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4">
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform"
                      style={{
                        background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)`,
                        boxShadow: `0 4px 12px -2px ${colors.brand.primary}40`
                      }}
                    >
                      <FolderIcon className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white group-hover:text-gray-100">{project.name}</h3>
                      {project.description && (
                        <p className="text-sm text-gray-400 mt-1 line-clamp-2">{project.description}</p>
                      )}
                    </div>
                  </div>
                  <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border ${getStatusColor(project.status)}`}>
                    {project.status.replace('_', ' ')}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="flex items-center gap-2">
                    <UserIcon className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Client</p>
                      <p className="text-sm text-gray-300 font-medium">
                        {project.party ? project.party.name : 'No client'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <CalendarDaysIcon className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Start Date</p>
                      <p className="text-sm text-gray-300 font-medium">
                        {project.start_date ? new Date(project.start_date).toLocaleDateString() : 'Not set'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <ClockIcon className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">End Date</p>
                      <p className="text-sm text-gray-300 font-medium">
                        {project.end_date ? new Date(project.end_date).toLocaleDateString() : 'Ongoing'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    Created {new Date(project.created_at).toLocaleDateString()}
                  </p>
                  <span className="text-sm font-medium text-blue-400 group-hover:text-blue-300 flex items-center gap-1">
                    View Details
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </a>
            ))}
            {projects.length === 0 && (
              <div className="text-center py-16 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <FolderIcon className="mx-auto h-16 w-16 text-gray-600" />
                <h3 className="mt-4 text-lg font-medium text-gray-300">No projects yet</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Get started by creating your first project.
                </p>
                <button
                  onClick={openCreateModal}
                  className="mt-6 inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white shadow-lg transition-all duration-200 hover:scale-105"
                  style={{
                    background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)`,
                    boxShadow: `0 4px 12px -2px ${colors.brand.primary}40`
                  }}
                >
                  <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                  Create First Project
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowCreateModal(false)}>
              <div className="absolute inset-0 bg-black bg-opacity-50"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>
            <div className="inline-block align-bottom bg-white portal-content-shadow rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Create New Project
              </h3>
              <form onSubmit={handleCreateProject}>
                <div className="space-y-4">
                  {/* Client Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Client <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      className="mt-1 block w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      value={formData.client_id}
                      onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                      disabled={loadingClients}
                    >
                      <option value="">Select a client...</option>
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.name} {client.type ? `(${client.type})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Template Toggle */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="useTemplate"
                      checked={useTemplate}
                      onChange={(e) => {
                        setUseTemplate(e.target.checked)
                        if (e.target.checked) {
                          fetchTemplates()
                        } else {
                          setSelectedTemplate('')
                          setFormData(prev => ({ ...prev, name: '', description: '' }))
                        }
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-2 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="useTemplate" className="ml-2 block text-sm text-gray-700">
                      Use a template
                    </label>
                  </div>

                  {/* Template Selection */}
                  {useTemplate && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Select Template
                      </label>
                      <div className="mt-1 flex gap-2">
                        <select
                          className="flex-1 block w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          value={selectedTemplate}
                          onChange={(e) => handleTemplateChange(e.target.value)}
                          disabled={loadingTemplates}
                        >
                          <option value="">Choose a template...</option>
                          {templates.map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.icon && `${template.icon} `}{template.name}
                            </option>
                          ))}
                        </select>
                        {selectedTemplate && (
                          <button
                            type="button"
                            onClick={() => {
                              const template = templates.find(t => t.id === selectedTemplate)
                              if (template) {
                                setPreviewTemplate(template)
                                setShowTemplatePreview(true)
                              }
                            }}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      {selectedTemplate && templates.find(t => t.id === selectedTemplate) && (
                        <p className="mt-2 text-sm text-gray-400">
                          {templates.find(t => t.id === selectedTemplate)?.description}
                        </p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Project Name
                    </label>
                    <input
                      type="text"
                      required
                      className="mt-1 block w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm placeholder-gray-400"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter project name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <textarea
                      rows={3}
                      className="mt-1 block w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm placeholder-gray-400"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Describe the project objectives"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Status
                    </label>
                    <select
                      className="mt-1 block w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    >
                      <option value="active">Active</option>
                      <option value="on_hold">On Hold</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Start Date
                      </label>
                      <input
                        type="date"
                        className="mt-1 block w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        End Date
                      </label>
                      <input
                        type="date"
                        className="mt-1 block w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        value={formData.end_date}
                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-lg px-4 py-2 text-base font-medium text-white shadow-lg transition-all duration-200 hover:scale-105 sm:col-start-2 sm:text-sm"
                    style={{
                      background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)`,
                      boxShadow: `0 4px 12px -2px ${colors.brand.primary}40`
                    }}
                  >
                    Create Project
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-gray-100 text-base font-medium text-gray-700 hover:bg-gray-200 transition-all duration-200 sm:mt-0 sm:col-start-1 sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Project Setup Wizard */}
      <ProjectSetupWizard
        isOpen={showSetupWizard}
        onClose={() => setShowSetupWizard(false)}
        initialClientId={preSelectedClientId || undefined}
        onProjectCreated={(projectId) => {
          fetchProjects()
          window.location.href = `/portal/projects/${projectId}`
        }}
      />

      {/* Template Preview Modal */}
      {showTemplatePreview && previewTemplate && (
        <TemplatePreview
          template={previewTemplate}
          onClose={() => {
            setShowTemplatePreview(false)
            setPreviewTemplate(null)
          }}
        />
      )}
    </div>
  )
}